// englishcontent2.js — Section 2: Reading and Writing Non-Fiction
// Improved Grade 9 Ready content:
// - Stronger KS3 → GCSE bridge
// - More explicit form / audience / purpose links
// - More exam-style analytical writing
// - Tiered worked examples
// - 5 practice questions per topic
// - Clear AI feedback criteria and mastery extension

const makeTopic = (topic) => ({
  duration: '30-45 mins',
  xp: 120,
  difficulty: 'foundation',
  ...topic,
});

const tier = (label, text, critique) => ({ label, text, critique });

export const englishContent2 = {
  id: 2,
  title: 'Reading and Writing Non-Fiction',
  slug: 'reading-and-writing-non-fiction',
  color: 'amber',
  icon: 'Newspaper',
  description:
    'Understand how non-fiction texts work and learn how to write letters, articles, speeches, leaflets and arguments for different audiences and purposes.',

  topics: [
    // =========================================================
    // TOPIC 1 — LAYOUT, FORM AND STRUCTURE
    // =========================================================
    makeTopic({
      id: 'eng-2-1',
      slug: 'layout-form-and-structure',
      title: 'Layout, Form and Structure',
      summary:
        'Understand how non-fiction texts are organised, presented and shaped for a reader.',

      explainer: {
        title: 'Layout, Form and Structure',
        content: `
Non-fiction texts are designed. Writers do not just choose words — they choose a form, a layout and a structure that help the text achieve its purpose.

FORM means the type of text. Common non-fiction forms include:
- Newspaper articles
- Magazine articles
- Letters
- Leaflets
- Reports
- Reviews
- Speeches
- Blogs
- Guides
- Advertisements

LAYOUT means how the text is arranged on the page or screen. Layout features include:
- Headlines
- Subheadings
- Columns
- Bullet points
- Images
- Captions
- Text boxes
- Bold, italics and colour
- Logos or branding
- Pull quotes

STRUCTURE means the order and development of ideas. A strong non-fiction text usually:
- Opens with a hook
- Establishes the topic quickly
- Builds ideas in a logical order
- Uses paragraphs to separate points
- Ends with a clear conclusion, summary or call to action

The best answers do not just name features. They explain why the writer has chosen them.

Weak:
"The article has a headline."

Stronger:
"The bold headline immediately signals the topic and grabs the reader’s attention, which suits the purpose of a magazine article because it needs to compete with other stories on the page."

Always link layout, form and structure to:
- Audience: Who is it for?
- Purpose: Why has it been written?
- Effect: What does it make the reader think, feel or do?
`,
      },

      interactiveLesson: {
        title: 'Layout, Form and Structure',
        steps: [
          {
            type: 'info',
            content:
              'A strong reader asks: why this form, why this layout, why this order?',
          },
          {
            type: 'question',
            question: 'What does a headline usually do?',
            options: [
              'Grabs attention and signals the topic',
              'Provides the final conclusion only',
              'Replaces the need for paragraphs',
              'Shows the writer’s address',
            ],
            correct: 0,
            explanation:
              'A headline introduces the topic and attracts the reader quickly.',
          },
          {
            type: 'question',
            question: 'Which feature is most useful for helping readers scan information quickly?',
            options: ['Bullet points', 'One long paragraph', 'Tiny print', 'A hidden caption'],
            correct: 0,
            explanation:
              'Bullet points separate key information so the reader can process it quickly.',
          },
          {
            type: 'question',
            question: 'What is structure?',
            options: [
              'The order and development of ideas',
              'The colour of the page',
              'The type of font used',
              'The photograph chosen',
            ],
            correct: 0,
            explanation:
              'Structure is about how the writer sequences and develops ideas.',
          },
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
A magazine article about teenage sleep uses a bold headline, a large photograph of a tired student, two columns and three subheadings.

Question:
How do the layout features help the article communicate its message?

Foundation answer:
The article uses a bold headline to grab attention. The photograph helps the reader understand the topic because it shows a tired student. The subheadings split the article into sections.

Developing answer:
The bold headline immediately draws attention to the issue of teenage sleep. The photograph makes the topic feel relatable because readers can see a student who looks exhausted. The subheadings organise the article into clear sections, helping the reader scan for information.

Grade 7-9 style answer:
The layout creates a professional and accessible magazine style. The bold headline acts as an immediate hook, while the image of the tired student gives the issue a human focus, making the topic feel relevant to a teenage audience. The two-column layout imitates the conventions of magazine journalism, and the subheadings guide the reader through the argument in manageable stages.
`,
      },

      workedExamples: [
        tier(
          'Foundation',
          'The article uses a headline to grab attention and subheadings to organise the information.',
          'This identifies features and gives a simple effect.'
        ),
        tier(
          'Secure',
          'The headline grabs attention immediately, while the subheadings break the article into clear sections so the reader can follow the information more easily.',
          'This links the features to reader effect.'
        ),
        tier(
          'Grade 7-9',
          'The writer uses a headline as an immediate hook and subheadings as signposts, creating a layout that feels accessible and purposeful. This suits a teenage audience because the information is broken into manageable sections rather than presented as a dense block of text.',
          'This links feature, effect, audience and purpose.'
        ),
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Which is a form of non-fiction text?',
          options: ['Letter', 'Metaphor', 'Simile', 'Adjective'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which feature is most associated with a newspaper article?',
          options: ['Headline', 'Stage direction', 'Chapter title', 'Rhyming couplet'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'A leaflet often uses bullet points because they:',
          options: [
            'Make key information easy to scan',
            'Make the text sound like poetry',
            'Hide the main argument',
            'Remove the need for a title',
          ],
          correct: 0,
        },
        {
          type: 'true-false',
          question: 'Structure means the order in which ideas are developed.',
          correct: true,
        },
        {
          type: 'true-false',
          question: 'Layout features should be discussed without mentioning audience or purpose.',
          correct: false,
        },
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Describe how a leaflet persuading young people to exercise could use layout, form and structure effectively.

Write 4-5 sentences. Mention:
- at least two layout features
- the audience
- the purpose
- the effect on the reader
`,
        sentenceStarter:
          'The leaflet would use a bold headline to grab the attention of young people...',
        hints: [
          'Name a layout feature and explain its effect.',
          'Use the words "audience" and "purpose".',
          'Explain why a leaflet is a suitable form.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Names at least two layout features',
          'Explains the effect of each feature',
          'Links choices to audience',
          'Links choices to purpose',
          'Uses accurate terminology: form, layout or structure',
        ],
      },

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your answer by adding:
- one sentence about why a leaflet is the right form
- one sentence explaining how the structure should build towards action
- one reference to the reader
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why might a report and a blog about the same topic look completely different on the page, even if they contain similar information?
`,
        xpReward: 60,
      },
    }),

    // =========================================================
    // TOPIC 2 — LANGUAGE IN NON-FICTION TEXTS
    // =========================================================
    makeTopic({
      id: 'eng-2-2',
      slug: 'language-in-non-fiction-texts',
      title: 'Language in Non-Fiction Texts',
      summary:
        'Explore how writers use facts, opinions, direct address, rhetorical questions and emotive language.',

      explainer: {
        title: 'Language in Non-Fiction Texts',
        content: `
Non-fiction writers choose language to suit purpose and audience.

Common language features include:
- Facts: information that can be checked
- Statistics: numbers or percentages used as evidence
- Opinions: personal views or judgements
- Expert quotations: comments from someone with authority
- Direct address: "you", "your", "we"
- Rhetorical questions: questions asked for effect
- Emotive language: words designed to make the reader feel something
- Lists of three: three linked words or phrases for emphasis
- Imperatives: command verbs, such as "act", "listen", "join"
- Modal verbs: "could", "should", "must", "might"
- Technical vocabulary: specialist words linked to the topic

Different purposes often use different language:

PERSUADE:
Uses emotive language, direct address, rhetorical questions and confident statements.

INFORM:
Uses facts, statistics, clear explanations and neutral vocabulary.

ADVISE:
Uses modal verbs and helpful direct address: "You could...", "You should..."

EXPLAIN:
Uses cause-and-effect connectives: "because", "therefore", "as a result", "this means..."

ARGUE:
Uses a clear viewpoint, evidence, counter-argument and logical development.

The best answers analyse the writer’s choices.

Weak:
"The writer uses a rhetorical question."

Stronger:
"The rhetorical question forces the reader to consider their own responsibility, making the argument feel more personal and difficult to ignore."
`,
      },

      interactiveLesson: {
        title: 'Language in Non-Fiction',
        steps: [
          {
            type: 'question',
            question: '"9 out of 10 students feel tired in lessons" is a:',
            options: ['Statistic', 'Emotive word', 'Rhetorical question', 'Command'],
            correct: 0,
            explanation: 'A statistic uses numerical evidence.',
          },
          {
            type: 'question',
            question: '"Surely we can do better?" is a:',
            options: ['Rhetorical question', 'Fact', 'Statistic', 'Caption'],
            correct: 0,
            explanation:
              'It is asked for effect, not because the writer expects a direct answer.',
          },
          {
            type: 'question',
            question: '"Devastating, heartbreaking, cruel" is an example of:',
            options: ['Emotive language and list of three', 'Technical vocabulary', 'Neutral fact', 'Chronology'],
            correct: 0,
            explanation:
              'The words are emotionally loaded and arranged in a three-part list.',
          },
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
"Over 60% of young people say they feel pressured by social media. Isn’t it time we listened to them?"

Question:
How does the writer make the issue seem important?

Foundation answer:
The writer uses a statistic, "Over 60%", to show that many young people are affected. The writer also uses a rhetorical question.

Developing answer:
The statistic "Over 60%" makes the issue seem important because it suggests the problem affects a large number of young people. The rhetorical question "Isn’t it time we listened?" pushes the reader to agree that action is needed.

Grade 7-9 style answer:
The writer combines statistical evidence with emotional pressure. The statistic "Over 60%" gives the argument authority, suggesting that this is not an isolated problem. The rhetorical question then shifts responsibility onto the reader, making inaction seem unreasonable. This blend of evidence and moral challenge makes the issue feel both factual and urgent.
`,
      },

      workedExamples: [
        tier(
          'Foundation',
          'The writer uses a statistic to show lots of young people are affected.',
          'This identifies a technique and gives a simple effect.'
        ),
        tier(
          'Secure',
          'The statistic "Over 60%" makes the issue seem serious because it suggests the problem affects the majority of young people.',
          'This uses quotation and explains effect.'
        ),
        tier(
          'Grade 7-9',
          'By placing the statistic before the rhetorical question, the writer first establishes credibility and then challenges the reader morally. This structure makes the argument feel both evidence-based and emotionally urgent.',
          'This analyses how techniques work together.'
        ),
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Which word is most emotive?',
          options: ['Walked', 'Devastated', 'Observed', 'Arrived'],
          correct: 1,
        },
        {
          type: 'mcq',
          question: 'Which feature is common in an advice guide?',
          options: ['Modal verbs', 'Stage directions', 'Rhyming couplets', 'Dialogue tags'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: '"You must act now" uses:',
          options: ['Direct address and imperative', 'A simile', 'A caption', 'A statistic'],
          correct: 0,
        },
        {
          type: 'true-false',
          question: 'Direct address uses words like "you", "your" and sometimes "we".',
          correct: true,
        },
        {
          type: 'true-false',
          question: 'A writer can only use one technique at a time.',
          correct: false,
        },
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Read this line:

"Our rivers are choking with plastic, and we, the children of this town, refuse to stay silent."

Identify two language features and explain their effects on the reader.
`,
        sentenceStarter:
          'The writer uses emotive language in the phrase "choking with plastic" to...',
        hints: [
          'Use short quotations.',
          'Name each technique before explaining it.',
          'Explain what the reader is made to feel or think.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Names at least two language features',
          'Uses short quotations accurately',
          'Explains effect on the reader',
          'Links to purpose',
          'Explains how the techniques strengthen the message',
        ],
      },

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your answer by explaining how the phrase "we, the children of this town" affects the reader’s attitude towards the speaker.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why might a writer mix facts and emotive language in the same argument?
`,
        xpReward: 60,
      },
    }),

    // =========================================================
    // TOPIC 3 — ANSWERING QUESTIONS ABOUT NON-FICTION
    // =========================================================
    makeTopic({
      id: 'eng-2-3',
      slug: 'answering-questions-about-non-fiction',
      title: 'Answering Questions About Non-Fiction',
      summary:
        'Develop exam-style reading skills: select evidence, analyse methods and explain effects.',

      explainer: {
        title: 'Answering Questions About Non-Fiction',
        content: `
Non-fiction reading questions usually test two things:

1. WHAT the writer says
This means your understanding of information, ideas and viewpoint.

2. HOW the writer says it
This means language, structure, tone, form and methods.

A strong approach:
- Read the question carefully.
- Underline the focus words.
- Read the text twice.
- Select short, precise quotations.
- Plan before you write.
- Use the wording of the question in your answer.

A useful paragraph structure is:

POINT:
Make a clear point about the writer’s method or message.

EVIDENCE:
Use a short quotation.

EXPLAIN:
Explain what the method suggests, implies or makes the reader think.

DEVELOP:
Link to purpose, audience, tone or wider effect.

Example sentence frame:
"The writer presents ___ as ___ through the phrase '___', which suggests ___. This encourages the reader to ___ because ___."

Avoid:
- Copying long chunks
- Feature spotting without explaining effect
- Saying "it makes the reader want to read on" for everything
- Writing about the topic generally instead of answering the question
`,
      },

      interactiveLesson: {
        title: 'Answering Questions',
        steps: [
          {
            type: 'question',
            question: 'The first step when answering a non-fiction question is to:',
            options: ['Read the question carefully', 'Start writing immediately', 'Copy the first quotation', 'Count the paragraphs'],
            correct: 0,
            explanation:
              'You need to understand the focus of the question before selecting evidence.',
          },
          {
            type: 'question',
            question: 'Which paragraph structure is strongest?',
            options: ['Point → Evidence → Explain → Develop', 'Quote → Quote → Quote', 'Opinion → Summary → Guess', 'Title → Date → Image'],
            correct: 0,
            explanation:
              'This structure helps you make a point, prove it and explain its effect.',
          },
          {
            type: 'question',
            question: 'Good evidence is usually:',
            options: ['A short, relevant quotation', 'A whole paragraph copied out', 'A random sentence', 'The longest phrase available'],
            correct: 0,
            explanation:
              'Short quotations are easier to analyse closely.',
          },
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Question:
How does the writer make the problem of traffic pollution feel urgent?

Text:
"Every day, thousands of children walk to school through streets choked with exhaust fumes. Surely this cannot go on."

Foundation answer:
The writer makes pollution seem urgent by saying "thousands of children" are affected. The word "choked" is emotive.

Developing answer:
The writer makes the problem feel urgent by using the emotive verb "choked", which suggests the streets are suffocating. The phrase "thousands of children" also shows that the problem affects many young people.

Grade 7-9 style answer:
The writer creates urgency by combining scale with emotional danger. The phrase "thousands of children" suggests that this is a widespread problem, while the verb "choked" personifies the streets as if they are suffocating. The rhetorical question "Surely this cannot go on" pressures the reader to accept that immediate action is necessary.
`,
      },

      workedExamples: [
        tier(
          'Foundation',
          'The writer uses the word "choked" to show pollution is bad.',
          'This spots a relevant word but needs more explanation.'
        ),
        tier(
          'Secure',
          'The verb "choked" suggests the fumes are dangerous and suffocating, making the streets seem unsafe for children.',
          'This explains the connotations of the quotation.'
        ),
        tier(
          'Grade 7-9',
          'The writer links vulnerable children with the suffocating verb "choked", creating a moral urgency: the reader is encouraged to see traffic pollution not as an inconvenience, but as a threat to young people’s safety.',
          'This develops effect and links to purpose.'
        ),
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Which quotation is better evidence?',
          options: ['"choked with exhaust fumes"', 'The whole paragraph copied out', 'A random sentence', 'No quotation'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which sentence explains effect best?',
          options: [
            'The word "choked" suggests danger and makes the pollution seem threatening.',
            'The writer uses a word.',
            'This is a quote.',
            'It makes the reader read on.',
          ],
          correct: 0,
        },
        {
          type: 'true-false',
          question: 'Linking to the writer’s purpose is a strong part of an answer.',
          correct: true,
        },
        {
          type: 'true-false',
          question: 'You should always use the longest quotation possible.',
          correct: false,
        },
        {
          type: 'true-false',
          question: 'A good answer focuses on the exact wording of the question.',
          correct: true,
        },
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Read this line:

"Our local library is not just a building — it is the heart of our community, and we cannot afford to lose it."

Write a short analytical paragraph explaining how the writer persuades the reader.
Use POINT → EVIDENCE → EXPLAIN → DEVELOP.
`,
        sentenceStarter:
          'The writer persuades the reader by presenting the library as...',
        hints: [
          'Use the quotation "heart of our community".',
          'Explain what the metaphor suggests.',
          'Link to the writer’s purpose.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Makes a clear point',
          'Uses a short quotation',
          'Explains connotations or implications',
          'Explains the effect on the reader',
          'Links to purpose or audience',
        ],
      },

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Add one sentence explaining why the phrase "we cannot afford to lose it" creates urgency.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why is the "Explain" part of P-E-E often the hardest part, but also the part that earns the most credit?
`,
        xpReward: 60,
      },
    }),

    // =========================================================
    // TOPIC 4 — WRITING LETTERS
    // =========================================================
    makeTopic({
      id: 'eng-2-4',
      slug: 'writing-letters',
      title: 'Writing Letters',
      summary:
        'Learn the conventions of letter writing and how tone changes depending on audience.',

      explainer: {
        title: 'Writing Letters',
        content: `
Letters are written directly to a person, group or organisation. The style depends on audience and purpose.

All effective letters need:
- A suitable greeting
- A clear reason for writing
- Organised paragraphs
- A suitable tone
- A clear ending
- An appropriate sign-off

Formal audiences include:
- Head teachers
- Councils
- Businesses
- Newspaper editors
- Employers
- Local MPs

Informal audiences include:
- Friends
- Family members
- People you know well

FORMAL LETTERS:
- Use polite, controlled language
- Avoid slang and emojis
- Avoid contractions where possible
- Use full sentences
- Use a clear paragraph structure
- End with "Yours sincerely" or "Yours faithfully"

INFORMAL LETTERS:
- Sound warmer and more personal
- Can use contractions
- Can include anecdotes
- Can ask friendly questions
- Still need clear structure and accurate writing

Important rule:
- Dear Sir or Madam → Yours faithfully
- Dear Mr/Mrs/Ms + surname → Yours sincerely

A strong letter sounds like it is written for one specific reader, not a random essay with "Dear..." added at the top.
`,
      },

      interactiveLesson: {
        title: 'Letter Basics',
        steps: [
          {
            type: 'question',
            question: '"Dear Mrs Patel" is an example of a:',
            options: ['Formal greeting', 'Informal greeting', 'Sign-off', 'Headline'],
            correct: 0,
            explanation:
              'It uses a title and surname, which is suitable for a formal letter.',
          },
          {
            type: 'question',
            question: '"Hi Amir, how are you?" is an example of:',
            options: ['Informal tone', 'Formal register', 'Technical vocabulary', 'A report heading'],
            correct: 0,
            explanation:
              'It sounds friendly and personal, so it suits an informal audience.',
          },
          {
            type: 'question',
            question: 'Which sign-off matches "Dear Sir or Madam"?',
            options: ['Yours faithfully', 'Love', 'Cheers mate', 'Yours sincerely'],
            correct: 0,
            explanation:
              'Use "Yours faithfully" when you do not know the recipient’s name.',
          },
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Task:
A student is writing to a local council about broken pavements.

Weak opening:
"Hi, the pavements are awful and you need to fix them."

Improved opening:
"Dear Sir or Madam,

I am writing to express my concern about the poor condition of several pavements near my school. Many students and elderly residents use these paths every day, and the uneven surfaces create a serious risk of injury."

Why this is stronger:
- The greeting is formal.
- The purpose is clear.
- The tone is polite but firm.
- The writer gives context.
- The issue is framed as a public safety concern.
`,
      },

      workedExamples: [
        tier(
          'Foundation',
          'Dear Sir or Madam, I am writing about broken pavements near my school.',
          'This is clear and formal, but it needs more detail.'
        ),
        tier(
          'Secure',
          'Dear Sir or Madam, I am writing to express my concern about the broken pavements near my school, which are becoming unsafe for pupils and local residents.',
          'This gives a clear purpose and explains why the issue matters.'
        ),
        tier(
          'Grade 7-9',
          'Dear Sir or Madam, I am writing to raise a serious safety concern regarding the damaged pavements on West Street, which are used daily by pupils, parents and elderly residents. The issue is no longer merely inconvenient; it presents a preventable risk of injury.',
          'This is precise, controlled and persuasive without becoming aggressive.'
        ),
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Which sign-off matches "Dear Sir or Madam"?',
          options: ['Yours faithfully', 'Yours sincerely', 'Love', 'See you soon'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'The first paragraph of a formal letter should usually:',
          options: ['State why you are writing', 'Tell a long unrelated story', 'Use slang', 'End the letter'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which greeting is most suitable for a letter to a named head teacher?',
          options: ['Dear Mrs Ahmed', 'Yo Ahmed', 'Hiya', 'To whoever'],
          correct: 0,
        },
        {
          type: 'true-false',
          question: 'Letters should always use the same tone, no matter who you write to.',
          correct: false,
        },
        {
          type: 'true-false',
          question: 'An informal letter should still be organised into paragraphs.',
          correct: true,
        },
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Plan the opening and closing of a formal letter to the head teacher, asking for better bike storage at school.

Include:
- formal greeting
- clear opening purpose
- appropriate sign-off
`,
        sentenceStarter:
          'Dear Mr/Mrs/Ms [Surname],\nI am writing to ask whether the school could consider...',
        hints: [
          'Use formal register.',
          'State your purpose in the first sentence.',
          'Use "Yours sincerely" if you have named the person.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Uses an appropriate greeting',
          'States the purpose clearly',
          'Maintains suitable tone',
          'Uses an appropriate sign-off',
          'Avoids slang and overly casual phrasing',
        ],
      },

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Add one sentence explaining why better bike storage would benefit pupils.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
What is the difference between "Yours sincerely" and "Yours faithfully", and why does it matter in formal writing?
`,
        xpReward: 60,
      },
    }),

    // =========================================================
    // TOPIC 5 — FORMAL LETTERS
    // =========================================================
    makeTopic({
      id: 'eng-2-5',
      slug: 'formal-letters',
      title: 'Formal Letters',
      summary:
        'Write formal letters with controlled register, clear structure and persuasive detail.',

      explainer: {
        title: 'Formal Letters',
        content: `
Formal letters are used for official or serious communication. They are often written to people in authority.

Examples:
- Letter to a council
- Letter to a head teacher
- Letter to a newspaper editor
- Letter of complaint
- Letter of application
- Letter to a company

A strong formal letter should:
- Use a formal greeting
- State the reason for writing immediately
- Use polite, controlled vocabulary
- Avoid slang, emojis and casual exaggeration
- Avoid contractions where possible
- Organise points into clear paragraphs
- Use evidence, examples or explanation
- End with a clear request, recommendation or conclusion

Useful formal phrases:
- I am writing to express my concern about...
- I would like to draw your attention to...
- I believe this issue requires urgent consideration.
- I would be grateful if you could...
- I hope you will consider this matter carefully.

Tone matters. Formal does not mean boring. It means controlled, precise and appropriate.

Weak:
"Your bins are a total joke."

Formal:
"I am writing to express my concern about the unreliable waste collection service in our area."
`,
      },

      interactiveLesson: {
        title: 'Formal Letters',
        steps: [
          {
            type: 'question',
            question: 'Which opening is most formal?',
            options: ['Dear Mrs Jones', 'Hiya Mrs Jones', 'Alright Jones', 'Yo Miss'],
            correct: 0,
            explanation:
              'A title and surname creates an appropriate formal tone.',
          },
          {
            type: 'question',
            question: 'Which should usually be avoided in a formal letter?',
            options: ['Contractions such as "don’t"', 'Clear paragraphs', 'Polite requests', 'Full sentences'],
            correct: 0,
            explanation:
              'Formal letters usually avoid contractions and use full forms like "do not".',
          },
          {
            type: 'question',
            question: 'A formal letter of complaint should sound:',
            options: ['Firm but polite', 'Insulting and aggressive', 'Random and chatty', 'Careless'],
            correct: 0,
            explanation:
              'A controlled tone makes the writer sound credible.',
          },
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Task:
Write the opening of a formal letter to the council asking for safer crossings near school.

Model:
Dear Sir or Madam,

I am writing to express my concern about the lack of safe pedestrian crossings near Greenbank School. Each morning, hundreds of pupils attempt to cross a busy road without the protection of a zebra crossing, traffic lights or a designated crossing patrol.

I believe a clearly marked crossing would significantly reduce the risk of accidents and give parents greater confidence that their children can travel to school safely.

Why it works:
- Formal greeting
- Clear purpose
- Specific issue
- Polite but serious tone
- Explains impact on pupils and parents
`,
      },

      workedExamples: [
        tier(
          'Foundation',
          'I am writing about the road near school because it is dangerous.',
          'This is clear but could be more formal and specific.'
        ),
        tier(
          'Secure',
          'I am writing to express my concern about the dangerous road outside our school, where many pupils cross each morning.',
          'This uses a formal phrase and gives context.'
        ),
        tier(
          'Grade 7-9',
          'I am writing to express my serious concern about the absence of a safe pedestrian crossing outside Greenbank School, despite the fact that hundreds of pupils use this route every morning.',
          'This is precise, formal and persuasive.'
        ),
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Which ending goes with "Dear Sir or Madam"?',
          options: ['Yours faithfully', 'Yours sincerely', 'Cheers', 'Best mate'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which phrase is most formal?',
          options: [
            'I am writing to express my concern...',
            'This is a total joke...',
            'Sort it out...',
            'It’s proper annoying...',
          ],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which sentence is most suitable for a formal complaint?',
          options: [
            'The repeated delays have caused significant inconvenience.',
            'Your company is useless.',
            'This is so annoying lol.',
            'I can’t even.',
          ],
          correct: 0,
        },
        {
          type: 'true-false',
          question: 'Formal letters should use contractions like "can’t" and "won’t".',
          correct: false,
        },
        {
          type: 'true-false',
          question: 'A formal letter can be persuasive while remaining polite.',
          correct: true,
        },
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Write the opening two sentences of a formal letter to the council about broken street lights in your area.
`,
        sentenceStarter:
          'Dear Sir or Madam,\nI am writing to express my concern about...',
        hints: [
          'Use formal register.',
          'Avoid contractions.',
          'Explain why the issue matters.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Uses a correct formal greeting',
          'States the purpose clearly',
          'Uses controlled formal register',
          'Avoids contractions and slang',
          'Explains why the issue matters',
        ],
      },

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Add one sentence that explains the impact of the broken street lights on local residents.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Rewrite this informally worded line in formal English:

"Hey — your bins are a total joke, can you sort them?"
`,
        xpReward: 60,
      },
    }),

    // =========================================================
    // TOPIC 6 — INFORMAL LETTERS
    // =========================================================
    makeTopic({
      id: 'eng-2-6',
      slug: 'informal-letters',
      title: 'Informal Letters',
      summary:
        'Write naturally and effectively for friends, family and personal audiences.',

      explainer: {
        title: 'Informal Letters',
        content: `
Informal letters are written to people you know well, such as friends or family.

They often:
- Use first names
- Open warmly
- Use contractions
- Include personal anecdotes
- Ask questions
- Use humour or excitement
- Use friendly sign-offs

Examples of informal greetings:
- Hi Leo,
- Dear Aisha,
- Hey Sam,

Examples of informal sign-offs:
- Take care,
- Speak soon,
- Love,
- Best wishes,

Informal does not mean careless. You still need:
- Clear paragraphs
- Accurate punctuation
- Clear meaning
- Controlled sentence structure
- A tone that suits the relationship

Too formal:
"I am writing to inform you of the events of my holiday."

Better:
"You will not believe what happened on holiday."

Too careless:
"omg it was mad and then yeah lol anyway"

Better:
"Honestly, it was chaotic — but in the best possible way."
`,
      },

      interactiveLesson: {
        title: 'Informal Letters',
        steps: [
          {
            type: 'question',
            question: 'Which greeting is informal?',
            options: ['Hi Priya', 'Dear Sir or Madam', 'To the manager', 'Dear Councillor Khan'],
            correct: 0,
            explanation:
              'Using a first name and friendly greeting creates an informal tone.',
          },
          {
            type: 'question',
            question: 'Which sign-off is informal?',
            options: ['Take care', 'Yours faithfully', 'Yours sincerely', 'Respectfully submitted'],
            correct: 0,
            explanation:
              '"Take care" is warm and suitable for a friend or family member.',
          },
          {
            type: 'question',
            question: 'Informal letters usually:',
            options: ['Use contractions', 'Avoid all personal details', 'Sound like reports', 'Use only statistics'],
            correct: 0,
            explanation:
              'Contractions such as "I’m" and "we’ll" help create a natural voice.',
          },
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Task:
Write the opening of an informal letter to a cousin about a school trip.

Weak:
"Dear cousin. I am writing to inform you that I attended a school trip."

Improved:
"Hi Jas,

You will not believe how good the trip was last week. I thought it was going to be boring, but it turned into one of the funniest days we have had all year."

Why it works:
- Friendly greeting
- Natural voice
- Personal reaction
- Direct connection with the reader
- Clear topic
`,
      },

      workedExamples: [
        tier(
          'Foundation',
          'Hi Jas, I went on a school trip last week and it was fun.',
          'This is clear and informal but basic.'
        ),
        tier(
          'Secure',
          'Hi Jas, you will not believe how good the school trip was last week. I thought it would be boring, but it ended up being hilarious.',
          'This sounds more natural and personal.'
        ),
        tier(
          'Grade 7-9',
          'Hi Jas, you know when you expect a school trip to be painfully dull and it somehow becomes the best day of the term? That was Friday.',
          'This uses voice, humour and direct connection with the reader.'
        ),
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Which phrase is informal?',
          options: ['Hope you’re doing well!', 'I trust you are in good health.', 'I am writing to complain.', 'Please find enclosed.'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which sentence has the most natural informal voice?',
          options: [
            'You should have seen what happened next.',
            'I am writing to notify you of subsequent events.',
            'The following report outlines my day.',
            'This correspondence concerns my birthday.',
          ],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which sign-off would suit a letter to a close friend?',
          options: ['Speak soon', 'Yours faithfully', 'Yours sincerely', 'Kind regards, Headteacher'],
          correct: 0,
        },
        {
          type: 'true-false',
          question: 'Informal letters should still have clear structure.',
          correct: true,
        },
        {
          type: 'true-false',
          question: 'Informal writing means spelling and punctuation do not matter.',
          correct: false,
        },
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Write the first three sentences of an informal letter to a friend telling them about a birthday celebration.
`,
        sentenceStarter:
          'Hi ___,\nYou will not believe what happened at my birthday...',
        hints: [
          'Use a friendly opening.',
          'Use at least one contraction.',
          'Include one personal detail.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Uses an informal greeting',
          'Sounds natural and personal',
          'Uses appropriate contractions',
          'Uses clear paragraphing or sentence control',
          'Avoids becoming careless or unclear',
        ],
      },

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Add one rhetorical question or direct comment to your friend to make the letter sound more personal.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why might you weaken an informal letter by making it too informal, for example by filling it with slang, abbreviations and unclear sentences?
`,
        xpReward: 60,
      },
    }),

    // =========================================================
    // TOPIC 7 — WRITING TO PERSUADE AND ARGUE
    // =========================================================
    makeTopic({
      id: 'eng-2-7',
      slug: 'writing-to-persuade-and-argue',
      title: 'Writing to Persuade and Argue',
      summary:
        'Construct convincing arguments using rhetorical devices, evidence and controlled structure.',

      explainer: {
        title: 'Writing to Persuade and Argue',
        content: `
Writing to persuade means trying to make the reader agree, act or change their view.

Writing to argue means presenting a clear viewpoint, often by considering both sides before reaching a judgement.

Persuasive techniques can be remembered with A FOREST:
- Alliteration
- Facts
- Opinions
- Rhetorical questions
- Emotive language
- Statistics
- Three / triplets

Other powerful techniques:
- Direct address: "you", "we", "our"
- Imperatives: "Act now", "Think again"
- Repetition: repeating key phrases
- Anecdote: a short personal example
- Counter-argument: acknowledging the other side
- Contrast: showing difference between what is and what could be

A strong persuasive structure:
1. Hook the reader.
2. State your viewpoint clearly.
3. Develop your first reason.
4. Add evidence or example.
5. Acknowledge and challenge the opposing view.
6. End with a memorable final sentence or call to action.

Good persuasive writing is not just a list of techniques. It should sound controlled, confident and purposeful.

Weak:
"School clubs are good. We need more. It would be fun."

Stronger:
"Every student deserves a place to belong after the final bell. More clubs would build confidence, reduce loneliness and give pupils a reason to feel proud of school."
`,
      },

      interactiveLesson: {
        title: 'Persuasive Techniques',
        steps: [
          {
            type: 'question',
            question:
              '"Safer schools, stronger students, smarter futures" is an example of:',
            options: ['Alliteration and list of three', 'Statistic only', 'Technical vocabulary', 'A formal sign-off'],
            correct: 0,
            explanation:
              'The repeated "s" sound is alliteration, and the sentence uses three linked phrases.',
          },
          {
            type: 'question',
            question: '"80% of pupils say..." is a:',
            options: ['Statistic', 'Opinion', 'Rhetorical question', 'Greeting'],
            correct: 0,
            explanation:
              'A percentage is a statistic and can make an argument sound evidence-based.',
          },
          {
            type: 'question',
            question: '"Isn’t it time we took action?" is a:',
            options: ['Rhetorical question', 'Fact', 'Caption', 'Subheading'],
            correct: 0,
            explanation:
              'It pushes the reader to agree without expecting a literal answer.',
          },
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
"Our libraries are shrinking, our pavements are cracked, and our parks are neglected. Surely it is time the council listened to the people who actually live here? Over 2,000 residents have signed the petition — and still, nothing has changed."

Question:
How does the writer build a persuasive argument?

Foundation answer:
The writer uses a list of three to show problems. The rhetorical question asks the council to listen. The statistic shows lots of people agree.

Developing answer:
The list of three, "libraries are shrinking, pavements are cracked, and parks are neglected", makes the area seem badly ignored. The rhetorical question challenges the council, while the statistic "Over 2,000 residents" gives evidence that many people want change.

Grade 7-9 style answer:
The writer builds pressure by layering emotional description with evidence. The list of three presents neglect as widespread, moving from libraries to pavements to parks to suggest that the whole community is affected. The rhetorical question directly challenges the council’s authority, while the statistic "Over 2,000 residents" gives the argument democratic weight. The final clause, "and still, nothing has changed", creates frustration and implies that inaction is unacceptable.
`,
      },

      workedExamples: [
        tier(
          'Foundation',
          'More clubs would be good because students would enjoy them.',
          'This gives a view but needs persuasive methods.'
        ),
        tier(
          'Secure',
          'More clubs would make school happier, healthier and more exciting. Why should students have to go home when they could be learning new skills?',
          'This uses a list of three and rhetorical question.'
        ),
        tier(
          'Grade 7-9',
          'After the final bell, too many students go home to screens, silence and missed opportunities. More clubs would not be a luxury; they would be an investment in confidence, friendship and ambition.',
          'This uses emotive contrast, a semi-colon and a powerful viewpoint.'
        ),
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Which A FOREST feature is "Surely you agree?"',
          options: ['Rhetorical question', 'Alliteration', 'Statistic', 'Fact'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which is the strongest persuasive sentence?',
          options: [
            'The sky is blue.',
            'Every child deserves a safe, happy and healthy start in life.',
            'School exists.',
            'There are clubs.',
          ],
          correct: 1,
        },
        {
          type: 'mcq',
          question: 'What does a counter-argument do?',
          options: [
            'Acknowledges the opposite view before challenging it',
            'Ends the text immediately',
            'Removes your own viewpoint',
            'Replaces evidence',
          ],
          correct: 0,
        },
        {
          type: 'true-false',
          question: 'Good persuasive writing usually sounds shouty and aggressive.',
          correct: false,
        },
        {
          type: 'true-false',
          question: 'A list of three can make a point sound more memorable.',
          correct: true,
        },
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Write a persuasive paragraph arguing that your school should offer more clubs.

Use at least two persuasive techniques.
`,
        sentenceStarter:
          'Our school deserves clubs that inspire, include and challenge every student...',
        hints: [
          'Use a rhetorical question.',
          'Use a list of three.',
          'Consider using direct address.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Uses at least two persuasive techniques',
          'Has a clear viewpoint',
          'Sounds confident but controlled',
          'Explains why the issue matters',
          'Uses deliberate vocabulary for impact',
        ],
      },

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Add a counter-argument sentence beginning:

"Some people may argue that..."
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Explain why acknowledging the opposite viewpoint can make your own argument stronger.
`,
        xpReward: 60,
      },
    }),

    // =========================================================
    // TOPIC 8 — WRITING TO INFORM, ADVISE AND EXPLAIN
    // =========================================================
    makeTopic({
      id: 'eng-2-8',
      slug: 'writing-to-inform-advise-and-explain',
      title: 'Writing to Inform, Advise and Explain',
      summary:
        'Communicate clearly for different non-fiction purposes using facts, guidance and cause-effect links.',

      explainer: {
        title: 'Writing to Inform, Advise and Explain',
        content: `
Informing, advising and explaining are related, but they are not the same.

INFORM:
Purpose: give clear information.
Language features:
- Facts
- Clear topic sentences
- Precise nouns and verbs
- Neutral tone
- Dates, places, names and details

Example:
"The library opens at 9am every weekday and closes at 5pm."

ADVISE:
Purpose: guide someone towards a sensible choice.
Language features:
- Direct address
- Modal verbs: should, could, might, may
- Reassuring tone
- Practical steps
- Imperatives used gently

Example:
"You should revise in short sessions and take regular breaks."

EXPLAIN:
Purpose: help the reader understand how or why something happens.
Language features:
- Cause-and-effect connectives
- Sequencing
- Examples
- Clear development
- Technical vocabulary when needed

Example:
"Sleep improves memory because the brain processes new information overnight."

Strong non-fiction often combines purposes:
A health leaflet might inform the reader about exercise, advise them to start slowly, and explain why activity improves concentration.
`,
      },

      interactiveLesson: {
        title: 'Inform / Advise / Explain',
        steps: [
          {
            type: 'question',
            question: '"The museum closes at 5pm." This purpose is to:',
            options: ['Inform', 'Persuade', 'Argue', 'Entertain'],
            correct: 0,
            explanation:
              'It gives clear factual information.',
          },
          {
            type: 'question',
            question: '"You should revise little and often." This purpose is to:',
            options: ['Advise', 'Inform only', 'Narrate', 'Describe a character'],
            correct: 0,
            explanation:
              'It uses "you should" to give guidance.',
          },
          {
            type: 'question',
            question:
              '"Plants need sunlight because photosynthesis produces food for them." This purpose is to:',
            options: ['Explain', 'Advise', 'Complain', 'Review'],
            correct: 0,
            explanation:
              'It uses "because" to explain why sunlight matters.',
          },
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
"Regular exercise is important for teenagers because it improves focus, reduces stress and builds stronger bones. You should aim for at least 60 minutes of activity each day."

Question:
Which purposes are being used?

Foundation answer:
The first sentence explains because it says why exercise is important. The second sentence advises because it says "You should".

Developing answer:
The first sentence explains the benefits of exercise by using the connective "because" and listing effects such as improved focus and reduced stress. The second sentence advises the reader by using the direct address "You" and the modal verb "should".

Grade 7-9 style answer:
The paragraph combines explanation and advice to make the information practical. The first sentence uses cause and effect to explain why exercise matters, while the second turns that explanation into a clear recommendation. This is effective for a teenage audience because it gives both the reason and the action.
`,
      },

      workedExamples: [
        tier(
          'Inform',
          'Most teenagers need regular sleep to help them concentrate during the day.',
          'This gives clear information.'
        ),
        tier(
          'Advise',
          'You should try to keep your bedtime consistent, even at weekends.',
          'This gives practical guidance using direct address and a modal verb.'
        ),
        tier(
          'Explain',
          'Sleep matters because the brain uses this time to process memories and recover from the day.',
          'This explains why sleep is important using cause and effect.'
        ),
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Which connective is common in explaining?',
          options: ['Because', 'Surely', 'Wow', 'Dear'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which phrase is common in advising?',
          options: ['You could try...', '80% of people...', 'Once upon a time...', 'Yours faithfully...'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which sentence informs?',
          options: [
            'The club meets every Tuesday at 3:30pm.',
            'Join now before it is too late!',
            'You should bring water.',
            'This happens because...',
          ],
          correct: 0,
        },
        {
          type: 'true-false',
          question: 'Informing writing is mostly opinion.',
          correct: false,
        },
        {
          type: 'true-false',
          question: 'A single text can inform, advise and explain.',
          correct: true,
        },
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Write three sentences about sleep:
1. One sentence that informs
2. One sentence that advises
3. One sentence that explains
`,
        sentenceStarter:
          'INFORM: Most teenagers...\nADVISE: You should...\nEXPLAIN: Sleep matters because...',
        hints: [
          'Use a fact for informing.',
          'Use "you should" or "you could" for advising.',
          'Use "because" or "as a result" for explaining.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Inform sentence gives clear information',
          'Advise sentence uses direct address or modal verbs',
          'Explain sentence uses cause and effect',
          'Each sentence has a distinct purpose',
          'Writing is clear and accurate',
        ],
      },

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Add one connective to make your explaining sentence more developed, such as "therefore", "as a result" or "this means that".
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
How can a health leaflet use inform, advise and explain on the same page without becoming confusing?
`,
        xpReward: 60,
      },
    }),

    // =========================================================
    // TOPIC 9 — WARM-UP AND WORKED PRACTICE QUESTIONS
    // =========================================================
    makeTopic({
      id: 'eng-2-9',
      slug: 'warm-up-and-worked-practice-questions-non-fiction',
      title: 'Warm-Up and Worked Practice Questions',
      summary:
        'Build confidence with short non-fiction analysis tasks before longer exam-style responses.',

      explainer: {
        title: 'Warm-Up and Worked Practice',
        content: `
Warm-up questions train the small skills that make longer answers stronger.

You will practise:
- Identifying purpose
- Spotting techniques
- Selecting short evidence
- Explaining effect
- Linking to audience
- Writing concise analytical sentences

A strong short answer often follows this pattern:

1. Name the method.
2. Use a short quotation.
3. Explain the effect.
4. Link to the writer’s purpose.

Example:
"The writer uses the emotive verb 'deserves' to suggest that a quiet study space is a basic right, not a luxury. This encourages the reader to support libraries because the issue feels fair and moral."

Notice:
- The quotation is short.
- The explanation focuses on effect.
- The answer links to purpose.
`,
      },

      interactiveLesson: {
        title: 'Warm-Up Questions',
        steps: [
          {
            type: 'question',
            question: 'A leaflet giving revision tips would mainly be written to:',
            options: ['Advise', 'Shock only', 'Tell a fictional story', 'Describe a character'],
            correct: 0,
            explanation:
              'Revision tips are guidance, so the purpose is mainly to advise.',
          },
          {
            type: 'question',
            question: '"90% of teens say they feel rushed in the mornings." This is a:',
            options: ['Statistic', 'Opinion', 'Metaphor', 'Command'],
            correct: 0,
            explanation:
              'A percentage is a statistic.',
          },
          {
            type: 'question',
            question: 'A worked answer usually uses:',
            options: ['Point, evidence and explanation', 'Only a quotation', 'Only a summary', 'No evidence'],
            correct: 0,
            explanation:
              'A worked answer models how to build analysis.',
          },
        ],
      },

      workedExample: {
        title: 'Worked Practice Question',
        content: `
Text:
"Every teenager deserves a quiet place to study. Libraries offer a free, focused environment and should be protected at all costs."

Question:
How does the writer try to persuade the reader?

Foundation answer:
The writer uses the word "deserves" to show teenagers should have a place to study. The phrase "protected at all costs" sounds urgent.

Developing answer:
The writer persuades the reader by using the emotive verb "deserves", which makes study space sound like a right. The phrase "protected at all costs" creates urgency and shows the writer feels strongly about libraries.

Grade 7-9 style answer:
The writer frames libraries as a moral necessity rather than an optional service. The emotive verb "deserves" implies that denying teenagers a quiet place to study would be unfair, while "protected at all costs" creates a tone of urgency. This encourages the reader to see library closures as a serious threat to young people’s opportunities.
`,
      },

      workedExamples: [
        tier(
          'Basic identification',
          'The writer uses "deserves".',
          'This spots a useful word but does not explain it.'
        ),
        tier(
          'Secure explanation',
          'The word "deserves" suggests teenagers have a right to a quiet place to study.',
          'This explains the implication of the word.'
        ),
        tier(
          'Developed analysis',
          'The emotive verb "deserves" turns the issue into a question of fairness, making the reader more likely to support libraries because they seem essential to young people’s chances.',
          'This links word choice to purpose and reader response.'
        ),
      ],

      practice: [
        {
          type: 'mcq',
          question: '"Every teenager deserves a quiet place..." — the word "deserves" is:',
          options: ['Emotive', 'A statistic', 'A layout feature', 'A date'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: '"Protected at all costs" sounds:',
          options: ['Confident and urgent', 'Casual and uncertain', 'Humorous only', 'Neutral'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which is the best analytical sentence?',
          options: [
            'The phrase "at all costs" creates urgency and suggests the issue is too important to ignore.',
            'The writer uses words.',
            'This is persuasive because it is persuasive.',
            'The quote is good.',
          ],
          correct: 0,
        },
        {
          type: 'true-false',
          question: 'Warm-up questions help prepare for longer exam answers.',
          correct: true,
        },
        {
          type: 'true-false',
          question: 'A short answer never needs evidence.',
          correct: false,
        },
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Read this line:

"Our high street is tired, empty and forgotten — but it does not have to stay that way."

Write a short paragraph explaining how the writer tries to persuade the reader.
`,
        sentenceStarter:
          'The writer persuades the reader by using a list of three...',
        hints: [
          'Use the quotation "tired, empty and forgotten".',
          'Comment on the hopeful ending.',
          'Explain the effect on the reader.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Makes a clear point',
          'Uses a short quotation',
          'Names at least one technique',
          'Explains reader effect',
          'Comments on tone or purpose',
        ],
      },

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Add one sentence explaining how the phrase "it does not have to stay that way" changes the tone.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
What makes a short, punchy final line persuasive?
`,
        xpReward: 60,
      },
    }),

    // =========================================================
    // TOPIC 10 — WORKED PRACTICE QUESTIONS
    // =========================================================
    makeTopic({
      id: 'eng-2-10',
      slug: 'worked-practice-questions-non-fiction',
      title: 'Worked Practice Questions',
      summary:
        'Study model responses and learn how to move from basic comments to developed analysis.',

      explainer: {
        title: 'Worked Practice Questions',
        content: `
A worked practice question shows you what a strong answer looks like.

When studying a model answer, look for:
- A clear opening point
- Short embedded quotations
- Accurate technique names
- Explanation of effect
- Comments on tone
- Links to purpose and audience
- Development beyond feature spotting

Strong analytical writing often uses verbs such as:
- suggests
- implies
- presents
- emphasises
- reinforces
- challenges
- encourages
- positions
- contrasts
- foregrounds

Weak analytical phrase:
"This makes the reader want to read on."

Better analytical phrase:
"This positions the reader to distrust the council’s claim because the writer immediately contrasts official reassurance with visible evidence of neglect."

Aim to explain how the method works, not just what the method is.
`,
      },

      interactiveLesson: {
        title: 'Study the Model',
        steps: [
          {
            type: 'question',
            question: 'A strong model answer usually opens with:',
            options: ['One clear point linked to the question', 'A long general introduction', 'A random quotation', 'A greeting'],
            correct: 0,
            explanation:
              'A clear opening point keeps the answer focused.',
          },
          {
            type: 'question',
            question: 'Why should you link to the writer’s purpose?',
            options: ['To show deeper understanding', 'To fill space', 'To avoid evidence', 'To change topic'],
            correct: 0,
            explanation:
              'Purpose explains why the writer has made particular choices.',
          },
          {
            type: 'question',
            question: 'Short quotations should be:',
            options: ['Embedded inside your sentences', 'Copied as whole paragraphs', 'Avoided completely', 'Used without explanation'],
            correct: 0,
            explanation:
              'Embedding quotations helps your analysis flow naturally.',
          },
        ],
      },

      workedExample: {
        title: 'Worked Model Answer',
        content: `
Text:
"The council insists that everything is under control. But walk down any street after dark and you will see a very different picture — shadows where streetlights should be."

Question:
How does the writer challenge the council?

Foundation answer:
The writer says the council thinks everything is under control, but the streets are dark. This challenges them because it shows they are wrong.

Developing answer:
The writer challenges the council by contrasting their claim that "everything is under control" with the image of "shadows where streetlights should be". This makes the council sound out of touch because the writer suggests the reality is different from what they say.

Grade 7-9 style answer:
The writer undermines the council by exposing a gap between official reassurance and lived reality. The phrase "everything is under control" sounds confident, but it is immediately challenged by the direct address "walk down any street", which invites the reader to test the claim personally. The image of "shadows where streetlights should be" symbolises neglect and makes the council appear detached from the community’s everyday experience.
`,
      },

      workedExamples: [
        tier(
          'Foundation',
          'The writer says the council is wrong because there are shadows.',
          'This understands the basic challenge.'
        ),
        tier(
          'Secure',
          'The contrast between "everything is under control" and "shadows where streetlights should be" makes the council seem unreliable.',
          'This explains how contrast works.'
        ),
        tier(
          'Grade 7-9',
          'The writer uses contrast to undermine institutional authority: the council’s confident claim is placed against a visual image of darkness, making official reassurance seem hollow and disconnected from residents’ experience.',
          'This uses precise analytical vocabulary and explores implication.'
        ),
      ],

      practice: [
        {
          type: 'mcq',
          question: 'The contrast between "under control" and "shadows" creates:',
          options: ['Distrust of the council', 'A peaceful atmosphere', 'A comic tone', 'No effect'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Direct address like "walk down any street" is used to:',
          options: ['Invite the reader to imagine or test the claim', 'End the article', 'Create a formal greeting', 'Show a statistic'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which word best describes the tone of the extract?',
          options: ['Challenging', 'Celebratory', 'Carefree', 'Romantic'],
          correct: 0,
        },
        {
          type: 'true-false',
          question: 'A strong model answer links techniques to their effects.',
          correct: true,
        },
        {
          type: 'true-false',
          question: 'The best answers only name techniques and do not explain them.',
          correct: false,
        },
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Read this line:

"We were promised new buses. We received broken promises."

Write a short paragraph analysing how the writer challenges authority.
`,
        sentenceStarter:
          'The writer challenges authority through repetition and contrast...',
        hints: [
          'Comment on the repeated sentence structure.',
          'Explain the phrase "broken promises".',
          'Link to trust.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Makes a clear point',
          'Uses a short quotation',
          'Comments on repetition or contrast',
          'Explains effect on trust',
          'Links to the writer’s critical tone',
        ],
      },

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Add one sentence explaining why "broken promises" is more powerful than simply saying "the buses did not arrive".
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why does repetition make an argument feel more powerful?
`,
        xpReward: 60,
      },
    }),

    // =========================================================
    // TOPIC 11 — PRACTICE QUESTIONS
    // =========================================================
    makeTopic({
      id: 'eng-2-11',
      slug: 'practice-questions-non-fiction',
      title: 'Practice Questions',
      summary:
        'Apply your skills independently to non-fiction reading and analysis tasks.',

      explainer: {
        title: 'Practice Questions',
        content: `
Now you practise without a model.

Before writing:
- Read the question carefully.
- Underline the focus words.
- Read the extract twice.
- Pick two short quotations.
- Decide what each quotation shows.
- Plan one paragraph per point.

When writing:
- Start with an overview sentence.
- Use precise evidence.
- Explain the writer’s method.
- Analyse word choices.
- Comment on structure if relevant.
- Link to audience and purpose.

A strong answer is focused. Every sentence should help answer the question.

Useful analytical sentence stems:
- The writer presents ___ as ___.
- The phrase "___" suggests...
- The verb/adjective/noun "___" implies...
- This encourages the reader to...
- This links to the writer’s purpose because...
- The contrast between ___ and ___ highlights...
- The structure shifts from ___ to ___, which...
`,
      },

      interactiveLesson: {
        title: 'Practice Warm-Up',
        steps: [
          {
            type: 'question',
            question: 'Before writing, you should:',
            options: ['Plan your points', 'Start writing immediately', 'Ignore the question', 'Use the longest quote'],
            correct: 0,
            explanation:
              'Planning helps your answer stay focused and organised.',
          },
          {
            type: 'question',
            question: 'Every sentence in an exam answer should be:',
            options: ['Relevant to the question', 'About any related topic', 'As long as possible', 'A summary only'],
            correct: 0,
            explanation:
              'Irrelevant sentences do not help you gain marks.',
          },
          {
            type: 'question',
            question: 'Good answers often link to:',
            options: ['Purpose and audience', 'Only word count', 'The writer’s birthday', 'Random facts'],
            correct: 0,
            explanation:
              'Purpose and audience explain why choices are effective.',
          },
        ],
      },

      workedExample: {
        title: 'Worked Example',
        content: `
Text:
"Our town square used to be the heart of the community. Now, half the shops have boarded-up windows, and on Saturdays the only visitors are pigeons."

Question:
How does the writer present the town square?

Foundation answer:
The writer presents the town square as sad and empty. It used to be the "heart of the community", but now there are "boarded-up windows".

Developing answer:
The writer presents the town square as neglected by contrasting the past and present. The metaphor "heart of the community" suggests it used to be important and full of life, while "boarded-up windows" shows decline.

Grade 7-9 style answer:
The writer presents the town square as a place that has lost its social purpose. The metaphor "heart of the community" suggests it was once central to local identity, but this is sharply contrasted with the image of "boarded-up windows", which implies abandonment and economic decline. The humorous detail that "the only visitors are pigeons" makes the emptiness more vivid while also criticising the lack of human activity.
`,
      },

      workedExamples: [
        tier(
          'Foundation',
          'The square seems empty because the shops are boarded up.',
          'This understands the basic meaning.'
        ),
        tier(
          'Secure',
          'The image of "boarded-up windows" suggests the square has declined and is no longer busy.',
          'This explains the implication of the evidence.'
        ),
        tier(
          'Grade 7-9',
          'The writer contrasts the metaphorical warmth of "heart of the community" with the bleak image of "boarded-up windows", presenting the square as a place that has lost both economic life and emotional value.',
          'This analyses contrast and develops meaning.'
        ),
      ],

      practice: [
        {
          type: 'mcq',
          question: 'The phrase "heart of the community" suggests:',
          options: ['The square used to be important and lively', 'The square is now thriving', 'The square was never used', 'The square is a hospital'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: '"The only visitors are pigeons" uses:',
          options: ['Humour to emphasise emptiness', 'A statistic', 'Direct address', 'A formal greeting'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which quotation best shows decline?',
          options: ['"boarded-up windows"', '"heart of the community"', '"Saturdays"', '"used to be"'],
          correct: 0,
        },
        {
          type: 'true-false',
          question: 'You should link your points back to the question.',
          correct: true,
        },
        {
          type: 'true-false',
          question: 'An overview sentence can help show that you understand the whole extract.',
          correct: true,
        },
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Read this line:

"Our playgrounds are tired, our courts are cracked, and our nets hang like cobwebs."

Write a short paragraph analysing how the writer creates a sense of neglect.
`,
        sentenceStarter:
          'The writer creates a sense of neglect through a list of three...',
        hints: [
          'Use two short quotations.',
          'Comment on the simile "like cobwebs".',
          'Explain the effect of the list.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Makes a clear overview point',
          'Uses two short quotations',
          'Names a technique',
          'Explains effect on the reader',
          'Links to the idea of neglect',
        ],
      },

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Add one sentence explaining why "cobwebs" makes the facilities seem unused or forgotten.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why is an overview sentence a strong way to begin an analytical answer?
`,
        xpReward: 60,
      },
    }),

    // =========================================================
    // TOPIC 12 — KNOWLEDGE ORGANISER
    // =========================================================
    makeTopic({
      id: 'eng-2-12',
      slug: 'reading-and-writing-non-fiction-knowledge-organiser',
      title: 'Reading & Writing Non-Fiction — Knowledge Organiser',
      summary:
        'Review the key terminology, techniques and exam habits from Section 2.',

      explainer: {
        title: 'Knowledge Organiser',
        content: `
SECTION 2 CORE KNOWLEDGE

NON-FICTION FORMS:
- Article
- Letter
- Leaflet
- Report
- Review
- Speech
- Blog
- Guide
- Advertisement

LAYOUT FEATURES:
- Headline
- Subheading
- Bullet points
- Columns
- Images
- Captions
- Bold text
- Text boxes
- Pull quotes

STRUCTURE:
- Hook
- Topic introduction
- Logical development
- Paragraph sequence
- Contrast
- Shift in tone
- Conclusion
- Call to action

LANGUAGE FEATURES:
- Facts
- Statistics
- Opinions
- Expert quotations
- Direct address
- Rhetorical questions
- Emotive language
- Lists of three
- Imperatives
- Modal verbs
- Technical vocabulary

A FOREST:
- Alliteration
- Facts
- Opinions
- Rhetorical questions
- Emotive language
- Statistics
- Three / triplets

LETTER RULES:
- Dear Sir or Madam → Yours faithfully
- Dear Mr/Mrs/Ms + surname → Yours sincerely
- Formal letters use controlled, polite register.
- Informal letters sound personal but still need clear structure.

PURPOSES:
- Inform: give facts clearly
- Advise: guide the reader
- Explain: show how or why
- Persuade: make the reader agree or act
- Argue: present a viewpoint using reasoning and evidence

READING ANSWERS:
Use POINT → EVIDENCE → EXPLAIN → DEVELOP.

WRITING ANSWERS:
Always consider:
- Form
- Audience
- Purpose
- Tone
- Register
- Structure
- Vocabulary
- Sentence control

GRADE 7-9 HABIT:
Do not just identify methods. Explain how methods work together to shape the reader’s response.
`,
      },

      interactiveLesson: {
        title: 'Recall Quiz',
        steps: [
          {
            type: 'question',
            question: 'Which ending matches "Dear Mr Smith"?',
            options: ['Yours sincerely', 'Yours faithfully', 'Love', 'Cheers'],
            correct: 0,
            explanation:
              'Use "Yours sincerely" when you know and use the recipient’s name.',
          },
          {
            type: 'question',
            question: '"You should try..." is language for:',
            options: ['Advising', 'Narrating', 'Describing only', 'Reporting speech'],
            correct: 0,
            explanation:
              '"You should" gives guidance, so it is advising.',
          },
          {
            type: 'question',
            question: 'Which A FOREST feature is "Over 80% of residents..."?',
            options: ['Statistics', 'Alliteration', 'Opinion', 'Three'],
            correct: 0,
            explanation:
              'A percentage is a statistic.',
          },
          {
            type: 'question',
            question: 'An overview sentence at the start of an answer shows:',
            options: ['You understand the whole extract', 'You only read the first line', 'You do not need evidence', 'You are avoiding the question'],
            correct: 0,
            explanation:
              'A strong overview shows your understanding before you analyse details.',
          },
        ],
      },

      workedExample: {
        title: 'Worked Summary',
        content: `
Text:
"Our libraries are quiet, focused and free. Yet every year, three more close."

Question:
How does the writer persuade the reader?

Foundation answer:
The writer uses a list of three, "quiet, focused and free", to show libraries are good. The fact that "three more close" shows they are in danger.

Developing answer:
The list of three, "quiet, focused and free", creates a positive image of libraries. The short sentence "Yet every year, three more close" contrasts with this and makes the closures seem unfair.

Grade 7-9 style answer:
The writer first creates a positive, almost idealised image of libraries through the list of three, "quiet, focused and free". The abrupt contrast created by "Yet" then shifts the tone from appreciation to concern. By ending with the factual detail that "three more close", the writer makes the loss feel measurable and urgent.
`,
      },

      workedExamples: [
        tier(
          'Foundation',
          'The writer uses a list of three to show libraries are good.',
          'This identifies a technique and basic effect.'
        ),
        tier(
          'Secure',
          'The list "quiet, focused and free" makes libraries seem valuable because each adjective presents a different benefit.',
          'This explains how the list builds a positive image.'
        ),
        tier(
          'Grade 7-9',
          'The positive triplet "quiet, focused and free" is undercut by the abrupt shift "Yet", creating a contrast between what libraries offer and how easily they are being lost.',
          'This comments on language and structure together.'
        ),
      ],

      practice: [
        {
          type: 'mcq',
          question: '"Quiet, focused and free" is an example of:',
          options: ['A list of three', 'An oxymoron', 'A simile', 'A headline'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: '"three more close" acts as:',
          options: ['A fact / statistic', 'An opinion', 'A greeting', 'A metaphor'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which word creates contrast?',
          options: ['Yet', 'Quiet', 'Free', 'Libraries'],
          correct: 0,
        },
        {
          type: 'true-false',
          question: 'Informing writing usually avoids unsupported personal opinion.',
          correct: true,
        },
        {
          type: 'true-false',
          question: 'Purpose, audience and form should influence your writing choices.',
          correct: true,
        },
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `
Pick three ideas from Section 2 and explain how each one could improve your non-fiction writing.

You could choose:
- direct address
- rhetorical questions
- formal register
- clear paragraphing
- statistics
- modal verbs
- lists of three
`,
        sentenceStarter:
          'Firstly, direct address could improve my non-fiction writing because...',
        hints: [
          'Choose three clear ideas.',
          'Explain why each one helps the reader.',
          'Link each idea to audience or purpose.',
        ],
      },

      aiFeedback: {
        successCriteria: [
          'Chooses three relevant ideas',
          'Explains why each one helps',
          'Links to audience or purpose',
          'Uses accurate terminology',
          'Writes clearly and coherently',
        ],
      },

      improvementTask: {
        title: 'Improve Your Answer',
        prompt: `
Improve your response by adding one example sentence for each technique you chose.
`,
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `
Why is it useful to use facts and emotive language in the same piece of persuasive writing?
`,
        xpReward: 60,
      },
    }),
  ],
};

export const getEnglishContent2TopicCount = () => englishContent2.topics.length;

export default englishContent2;